/**
 * Read/write Executive Calendar settings on users/{executiveUid}.executiveCalendarSettings
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { DelegateCalendarSummary, ExecutiveCalendarSettings, SavedCalendarPlace } from '../types';

export const DEFAULT_EXECUTIVE_CALENDAR_SETTINGS: ExecutiveCalendarSettings = {
  savedPlaces: [],
};

export function newSavedPlaceId(): string {
  return `place_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Firestore rejects undefined field values — omit optional keys. */
export function executiveCalendarSettingsForFirestore(
  settings: ExecutiveCalendarSettings,
): ExecutiveCalendarSettings {
  const savedPlaces = settings.savedPlaces.map(place => {
    const row: SavedCalendarPlace = {
      id: place.id,
      label: place.label,
      address: place.address,
    };
    if (place.lat != null) row.lat = place.lat;
    if (place.lng != null) row.lng = place.lng;
    return row;
  });

  const payload: ExecutiveCalendarSettings = { savedPlaces };

  if (settings.homePlaceId) {
    payload.homePlaceId = settings.homePlaceId;
  }
  if (settings.defaultTravelCalendarId) {
    payload.defaultTravelCalendarId = settings.defaultTravelCalendarId;
  }

  return payload;
}

export async function loadExecutiveCalendarSettings(
  executiveUid: string,
): Promise<ExecutiveCalendarSettings> {
  const snap = await getDoc(doc(db, 'users', executiveUid));
  const raw = snap.data()?.executiveCalendarSettings as ExecutiveCalendarSettings | undefined;
  if (!raw) return { ...DEFAULT_EXECUTIVE_CALENDAR_SETTINGS, savedPlaces: [] };
  return {
    homePlaceId: raw.homePlaceId,
    defaultTravelCalendarId: raw.defaultTravelCalendarId,
    savedPlaces: Array.isArray(raw.savedPlaces) ? raw.savedPlaces : [],
  };
}

export async function saveExecutiveCalendarSettings(
  executiveUid: string,
  settings: ExecutiveCalendarSettings,
): Promise<void> {
  await updateDoc(doc(db, 'users', executiveUid), {
    executiveCalendarSettings: executiveCalendarSettingsForFirestore(settings),
  });
}

export function getPlaceById(
  settings: ExecutiveCalendarSettings,
  placeId: string | undefined,
): SavedCalendarPlace | undefined {
  if (!placeId) return undefined;
  return settings.savedPlaces.find(p => p.id === placeId);
}

export function getHomePlace(settings: ExecutiveCalendarSettings): SavedCalendarPlace | undefined {
  return getPlaceById(settings, settings.homePlaceId);
}

/** Resolve Travel calendar: explicit setting, then name match, then first writable. */
export function resolveTravelCalendarId(
  settings: ExecutiveCalendarSettings,
  calendars: DelegateCalendarSummary[],
  writableIds: string[],
): string | undefined {
  if (
    settings.defaultTravelCalendarId &&
    writableIds.includes(settings.defaultTravelCalendarId)
  ) {
    return settings.defaultTravelCalendarId;
  }
  const byName = calendars.find(
    c => writableIds.includes(c.id) && /^travel$/i.test((c.summary || '').trim()),
  );
  if (byName) return byName.id;
  return writableIds[0];
}

export function resolvePrimaryCalendarId(
  calendars: DelegateCalendarSummary[],
  writableIds: string[],
): string | undefined {
  const primary = calendars.find(c => c.primary && writableIds.includes(c.id));
  return primary?.id ?? writableIds[0];
}
